import { apiFetch } from '../auth/apiFetch.ts';
import { useQuery } from '@tanstack/react-query';

/* Flagged video-session recordings (PRD-0010 D11, #248).
 *
 * A RECORDING IS NOT A CAPTURE, and this file is one of the places that has to
 * keep saying so. A capture is the robot's own MCAP record, written to local
 * disk and authoritative (PRD-0002 D6). A recording here is the CLOUD-SIDE
 * composition of what an operator happened to be watching — it degrades exactly
 * when the field link does, which is during the incident somebody will later
 * want footage of.
 *
 * They are separate recordings of the same minutes, with different framing and
 * timing. Reconciling them is explicit work, never an assumption. */

export interface Recording {
  session_id: string;
  robot_id: string;
  /** Who flagged it. The session's opener, not everyone who watched. */
  started_by: string;
  camera_topic: string;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  duration_s: number | null;
  /** Derived server-side from the bucket's 30-day expiry, not stored. */
  expires_at: string;
  /** False while the session is still live: composition has not finalised. */
  playable: boolean;
}

export interface RecordingList {
  robot_id: string;
  recordings: Recording[];
  retention_days: number;
  /** False where the recording stack was never deployed. */
  recording_available: boolean;
}

async function fetchRecordings(robotId: string): Promise<RecordingList> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/video/recordings`,
  );
  if (!r.ok) throw new Error(`status ${r.status}`);
  return (await r.json()) as RecordingList;
}

export function useRecordings(robotId: string | undefined) {
  return useQuery({
    queryKey: ['video-recordings', robotId] as const,
    queryFn: () => fetchRecordings(robotId as string),
    enabled: !!robotId,
    /* A recording appears when its session ends, so the list is only
       interesting on that timescale. Nothing here is live. */
    refetchInterval: 30_000,
  });
}

export interface Playback {
  session_id: string;
  robot_id: string;
  /** An HLS media playlist whose segment URIs are already presigned. */
  playlist: string;
  expires_in_s: number;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  /** Always false. Present so the claim travels with the footage. */
  authoritative: boolean;
}

export class NotReadyError extends Error {}

async function fetchPlayback(robotId: string, sessionId: string): Promise<Playback> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/video/recordings/`
    + `${encodeURIComponent(sessionId)}/playback`,
  );
  /* 425 Too Early is its own case, not a generic failure: the recording exists
     and will become playable, so the console says "still finishing" rather than
     "gone". */
  if (r.status === 425) throw new NotReadyError('still finishing');
  if (!r.ok) throw new Error(`status ${r.status}`);
  return (await r.json()) as Playback;
}

/* Fetched only when an operator actually opens one: it mints presigned URLs for
 * every segment, so it is not something to do for a whole list on render.
 *
 * Not retried on NotReady — the answer will not change within a retry window,
 * and hammering it mints signatures nobody uses. */
export function usePlayback(
  robotId: string | undefined,
  sessionId: string | undefined,
) {
  return useQuery({
    queryKey: ['video-playback', robotId, sessionId] as const,
    queryFn: () => fetchPlayback(robotId as string, sessionId as string),
    enabled: !!robotId && !!sessionId,
    retry: (count, err) => !(err instanceof NotReadyError) && count < 2,
    /* The URLs inside expire (`expires_in_s`, an hour). Refetching on focus
       would re-mint mid-playback for no gain; the player asks again if a
       segment 403s. */
    refetchOnWindowFocus: false,
    staleTime: 45 * 60_000,
  });
}
